import { updateWithToast } from '@/clientOnlyUtils/clientUtils';

import type { RouterOutputs } from '@poseidon/api/types';

import { api } from '@/utils/api';

export type ContactSequenceEnrollment = RouterOutputs['sequence']['enrollmentGraphsForContact']['enrollments'][number];

/**
 * A contact's live sequence enrollments (each as a per-contact graph) plus what the rep can do to one of them
 * from the contact page — rewrite a step's text for this contact, or mark the to-do they're parked on done.
 * Shared by the List view's timelines and the Graph view's canvas so the two can't drift.
 */
export function useContactSequenceEnrollments(teamId: string, contactId: string) {
  const utils = api.useUtils();
  const graphsQuery = api.sequence.enrollmentGraphsForContact.useQuery(
    { teamId, contactId },
    { enabled: Boolean(contactId) },
  );
  const refresh = () => void utils.sequence.enrollmentGraphsForContact.invalidate({ teamId, contactId });
  const setStepContent = api.sequence.setEnrollmentStepContent.useMutation({ onSuccess: refresh });
  // Mark a parked call/reminder to-do done for THIS contact → advance the arm, then refetch so the step flips
  // to sent + the next one becomes current. (`sequence.completeTask` resolves the arm from the wait node id.)
  const completeTask = api.sequence.completeTask.useMutation({ onSuccess: refresh });

  const saveStepContent =
    (enrollment: ContactSequenceEnrollment) =>
    async (overrideKey: string, next: { body?: string; subject?: string }) => {
      await updateWithToast({
        loading: 'Saving message for this contact…',
        success: 'Saved for this contact',
        mutationFnAsync: () =>
          setStepContent.mutateAsync({
            teamId,
            contactId,
            sequenceId: enrollment.sequencePublicId,
            overrideKey,
            body: next.body ?? '',
            subject: next.subject,
          }),
      });
    };

  const completeManualStep = (enrollment: ContactSequenceEnrollment) => async (nodeId: string, stepKey: string) => {
    await updateWithToast({
      loading: 'Marking done…',
      // Same honesty as the builder's pile: nothing matched → say so (see useSequenceFaceActions).
      success: (data) =>
        data != null && data.completed === 0
          ? 'Nothing to mark done — that to-do already moved on'
          : 'Marked done — sequence advanced',
      mutationFnAsync: () =>
        completeTask.mutateAsync({ teamId, sequenceId: enrollment.sequencePublicId, contactId, nodeId, stepKey }),
    });
  };

  return { enrollments: graphsQuery.data?.enrollments ?? [], saveStepContent, completeManualStep };
}
